import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useModalStore } from '../../stores/modal.store';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Icon } from '../ui/Icon';
import { Helper } from '../ui/Helper';
import { useOverlayA11y } from '../../lib/overlay';
import { fmtEur, fmtDate } from '../../lib/utils';
import { Division, DIVISION_LABEL, ProjectModel, ProjectStatus, RiskLevel, type CreateProjectInput } from '@masamune/shared';

/**
 * Apertura di un progetto. Due strade, stessa schermata:
 *
 *  - **da zero**: si sceglie il cliente dall'anagrafica e si va avanti;
 *  - **da una trattativa vinta** (payload `dealId`): quasi tutto è già
 *    compilato e si aggiunge solo quello che in fase di vendita non si sapeva.
 *
 * Meno campi possibile all'apertura: cliente, nome, cosa si vende, quando si
 * consegna. Costi, team, milestone e marginalità si compilano dopo, dalla
 * scheda del progetto, quando si sanno davvero.
 *
 * Due cose che prima non andavano e non devono tornare:
 *  1. il passo "cliente" mostrava i **primi 6** clienti e basta: gli altri 65
 *     erano irraggiungibili. Adesso c'è la ricerca e si vede tutta l'anagrafica;
 *  2. i campi economici nascevano precompilati con cifre inventate (88.000 di
 *     ricavo, 51.300 di costi): chi premeva Invio si portava a casa un progetto
 *     finto. Adesso non si inventa niente — l'unico importo che compare è
 *     quello che la trattativa aveva già scritto.
 */

interface ClientLite {
  id: string;
  legalName: string;
  shortName: string;
  sector: string | null;
  vat: string | null;
  projectsCount?: number;
}
interface ClientPage { items: ClientLite[]; total: number }

/**
 * Una trattativa vinta da cui può nascere un progetto. Restano fuori quelle da
 * cui il progetto è già nato (il legame è 0..1: aprirne un secondo conterebbe
 * due volte gli stessi soldi) e i rinnovi, che prolungano un canone invece di
 * aprire una commessa.
 */
interface TrattativaVinta {
  id: string;
  title: string;
  value: number;
  clientId: string | null;
  companyName: string | null;
  expectedCloseDate: string | null;
  pipeline: { name: string; kind: string };
  project: { id: string; code: string } | null;
}

interface Answers {
  cliente: string | null;
  divisione: Division | null;
  tipologia: ProjectModel | null;
  name: string;
  startDate: string;
  deadline: string;
  /** Il venduto, solo quando si arriva da una trattativa: altrove resta null. */
  valore: number | null;
}

const STEPS = ['cliente', 'cosa', 'nome', 'consegna', 'output'] as const;
type StepKey = typeof STEPS[number];

/**
 * Quante domande si fanno davvero: l'ultimo step ("Ci siamo") è il riepilogo,
 * non una domanda. Il numero si conta, non si scrive a mano: scritto a mano
 * diceva "Quattro domande" sopra un contatore che segnava 05, e chi legge si
 * fida della frase o del contatore, comunque di uno dei due sbagliato.
 */
const NUMERO_A_PAROLE: Record<number, string> = { 1: 'Una', 2: 'Due', 3: 'Tre', 4: 'Quattro', 5: 'Cinque', 6: 'Sei', 7: 'Sette' };
const QUANTE_DOMANDE = NUMERO_A_PAROLE[STEPS.length - 1] ?? String(STEPS.length - 1);

const STEP_TITLE: Record<StepKey, string> = {
  cliente: 'Cliente',
  cosa: 'Cosa si vende',
  nome: 'Nome',
  consegna: 'Consegna',
  output: 'Riepilogo',
};

const DIVISIONI: Array<[Division, string]> = [
  [Division.MKT, 'Campagne, always-on, content, media buying'],
  [Division.PROD, 'Siti, ecommerce, web app, piattaforme'],
  [Division.DESIGN, 'Brand, UI/UX, design system, visual'],
  [Division.TALENT, 'Corsi Formatemp/Fondimpresa, placement'],
];

const MODELLI: Array<[ProjectModel, string]> = [
  [ProjectModel.ONE_SHOT, 'One-shot'],
  [ProjectModel.ABBONAMENTO, 'Abbonamento'],
  [ProjectModel.RETAINER, 'Retainer'],
  [ProjectModel.CONSULENZA, 'Consulenza'],
  [ProjectModel.CORSO, 'Corso'],
  [ProjectModel.RECRUITING, 'Recruiting'],
  [ProjectModel.SVILUPPO, 'Sviluppo proprietario'],
  [ProjectModel.MAINTENANCE, 'Maintenance'],
  [ProjectModel.MISTO, 'Modello misto'],
];

/** Risposta di `POST /deals/:id/project`: il progetto e, sul voucher, il canone. */
interface ConversionResult {
  project: { id: string; code: string; name: string };
  canone: { name: string; amountNet: number; mesi: number; totale: number; endDate: string | null } | null;
}

export function NewProjectFlow({ onClose, clientId, division }: { onClose: () => void; clientId?: string; division?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tfRef = useOverlayA11y();
  const closing = useModalStore((s) => s.closing);

  // Prefill dal payload del modal store. Sono DEFAULT modificabili (mai lock),
  // catturati una sola volta al mount. `dealId` è quello che cambia strada:
  // con lui il progetto nasce agganciato alla trattativa da cui viene.
  const [prefill] = useState(() => useModalStore.getState().payload);
  const fromQuoteId = typeof prefill?.quoteId === 'string' ? prefill.quoteId : undefined;

  /**
   * Le due strade stanno nella STESSA schermata: da zero si sceglie il cliente,
   * oppure si parte da una trattativa vinta e i dati sono già dentro.
   *
   * Chi arriva dalla scheda di una trattativa ha già il `dealId` nel payload.
   * Chi arriva dal pulsante "Nuovo progetto" no, e prima non aveva nessun modo
   * di dire "questo nasce da quella vendita": il legame si perdeva, e con lui
   * il fatto che quel valore doveva uscire dal potenziale commerciale. Qui lo
   * può scegliere, ed è per questo che `dealScelto` è uno stato e non una
   * costante presa dal payload.
   */
  const [dealScelto, setDealScelto] = useState<TrattativaVinta | null>(null);
  const dealIdPrefill = typeof prefill?.dealId === 'string' ? prefill.dealId : undefined;
  const dealId = dealScelto?.id ?? dealIdPrefill;
  const dealTitle = dealScelto?.title ?? (typeof prefill?.dealTitle === 'string' ? prefill.dealTitle : undefined);
  const presetName = typeof prefill?.title === 'string' ? prefill.title : '';
  const presetDeadline = typeof prefill?.deadline === 'string' ? prefill.deadline : '';
  const rawValue = Number(prefill?.value);
  const presetValore = Number.isFinite(rawValue) && rawValue > 0 ? rawValue : null;
  const presetClientId = clientId ?? (typeof prefill?.clientId === 'string' ? prefill.clientId : undefined);
  // Il canone che nascerà insieme al progetto (proposta del server, solo voucher)
  const canonePrevisto = (prefill?.canone ?? null) as { amountNet: number; mesi: number; totale: number } | null;

  const [answers, setAnswers] = useState<Answers>({
    cliente: presetClientId ?? null,
    divisione: (division as Division) ?? null,
    tipologia: null,
    name: presetName,
    startDate: '',
    deadline: presetDeadline,
    valore: presetValore,
  });

  // Chi arriva col cliente già scelto non deve ripassare dal primo passo
  const [idx, setIdx] = useState(presetClientId ? 1 : 0);
  const [created, setCreated] = useState<{ id: string; code?: string; name: string; clientId: string; canone: ConversionResult['canone'] } | null>(null);

  const step = STEPS[idx]!;

  // ── Anagrafica: tutta, con la ricerca ────────────────────────────────────
  const [search, setSearch] = useState('');
  const [debSearch, setDebSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  // Una pagina sola tiene tutta l'anagrafica di oggi (71 clienti, tetto 200)
  const { data: page, isLoading: clientiLoading } = useQuery({
    queryKey: ['clients-picker'],
    queryFn: () => api.get<ClientPage>('/clients?pageSize=200'),
    staleTime: 5 * 60_000,
  });
  // Se un giorno l'anagrafica supera la pagina, la ricerca la fa il server:
  // così nessun cliente resta irraggiungibile, che è il bug da cui si parte.
  const troncata = !!page && page.total > page.items.length;
  const { data: remote } = useQuery({
    queryKey: ['clients-picker', 'search', debSearch],
    queryFn: () => api.get<ClientPage>(`/clients?pageSize=200&search=${encodeURIComponent(debSearch)}`),
    enabled: troncata && debSearch.trim().length >= 2,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const tutti = page?.items ?? [];
  const pool = troncata && debSearch.trim().length >= 2 ? (remote?.items ?? []) : tutti;
  const clienti = useMemo(() => {
    const q = debSearch.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter((c) =>
      `${c.legalName} ${c.shortName} ${c.vat ?? ''} ${c.sector ?? ''}`.toLowerCase().includes(q));
  }, [pool, debSearch]);

  const clienteScelto = tutti.find((c) => c.id === answers.cliente)
    ?? pool.find((c) => c.id === answers.cliente)
    ?? null;

  // ── Le trattative vinte da cui può nascere un progetto ───────────────────
  // Si caricano solo quando servono davvero: chi arriva già da una trattativa
  // non deve sceglierne un'altra.
  const { data: vinteRaw } = useQuery({
    queryKey: ['deals', 'vinte-convertibili'],
    queryFn: () => api.get<{ items: TrattativaVinta[] }>('/deals?view=vinte&pageSize=100'),
    enabled: !dealIdPrefill,
    staleTime: 60_000,
  });
  const vinte = useMemo(
    () => (vinteRaw?.items ?? []).filter((d) => !d.project && d.pipeline.kind !== 'RINNOVO' && d.clientId),
    [vinteRaw],
  );

  /**
   * Partire da una trattativa vinta: si porta dentro quello che la vendita sa
   * già (cliente, nome, importo, data prevista) e si salta al passo dopo.
   * Divisione e modello NON si indovinano: in fase di vendita non si sapevano,
   * e li sceglie chi apre il progetto.
   */
  const partiDaTrattativa = (d: TrattativaVinta) => {
    setDealScelto(d);
    setAnswers((a) => ({
      ...a,
      cliente: d.clientId,
      name: d.title,
      valore: d.value > 0 ? d.value : null,
      deadline: d.expectedCloseDate ?? a.deadline,
    }));
    setTimeout(() => setIdx(1), 140);
  };

  // ── Navigazione: non si passa avanti a mani vuote ────────────────────────
  const completo: Record<StepKey, boolean> = {
    cliente: !!answers.cliente,
    cosa: !!answers.divisione && !!answers.tipologia,
    nome: !!answers.name.trim(),
    consegna: true, // la data si può mettere dopo
    output: true,
  };
  const puoAvanzare = completo[step];
  const next = () => { if (puoAvanzare) setIdx((i) => Math.min(i + 1, STEPS.length - 1)); };
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  const scegliCliente = (c: ClientLite) => {
    setAnswers((a) => ({ ...a, cliente: c.id }));
    setTimeout(() => setIdx((i) => Math.min(i + 1, STEPS.length - 1)), 140);
  };

  const createMut = useMutation({
    mutationFn: async (): Promise<ConversionResult> => {
      const comune = {
        name: answers.name.trim(),
        clientId: answers.cliente!,
        division: answers.divisione!,
        model: answers.tipologia!,
        startDate: answers.startDate || null,
        deadline: answers.deadline || null,
      };
      // Dalla trattativa: passa dalla rotta che scrive anche il legame e, sul
      // voucher, la riga di canone della gestione pratica.
      if (dealId) {
        return api.post<ConversionResult>(`/deals/${dealId}/project`, {
          ...comune,
          ...(answers.valore != null ? { revenue: answers.valore } : {}),
        });
      }
      // Da zero: nessun importo inventato. I costi si scrivono dopo, quando si
      // sanno — zero è la verità, 88.000 no. L'unico ricavo che compare è
      // quello che arriva da un preventivo accettato, che è un numero vero.
      const body: CreateProjectInput = {
        ...comune,
        status: ProjectStatus.PREVENTIVO,
        phase: 'Preventivo',
        progress: 0,
        revenue: answers.valore ?? 0,
        cost: 0,
        riskLevel: RiskLevel.LOW,
        marginThreshold: 20,
        tags: [],
        members: [],
        milestones: [],
      };
      const p = await api.post<{ id: string; code: string; name: string }>('/projects', body);
      return { project: p, canone: null };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      // Il valore è appena uscito dal potenziale commerciale: le trattative
      // (lista, riepilogo, scheda) vanno rilette.
      if (dealId) qc.invalidateQueries({ queryKey: ['deals'] });
      toast.ok('Progetto creato', res.project.name);
      setCreated({ id: res.project.id, code: res.project.code, name: res.project.name, clientId: answers.cliente!, canone: res.canone });
    },
    onError: (err) => toast.alert('Progetto non creato', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const finalize = () => {
    if (!answers.cliente || !answers.divisione || !answers.tipologia || !answers.name.trim()) {
      toast.warn('Manca qualcosa', 'Servono cliente, cosa si vende e il nome del progetto.');
      return;
    }
    createMut.mutate();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Enter' || step === 'output') return;
      // Sui campi dove Enter significa "ho finito di digitare" non si avanza
      const el = document.activeElement as HTMLElement | null;
      const type = (el as HTMLInputElement | null)?.type;
      if (el?.tagName === 'TEXTAREA') return;
      if (el?.tagName === 'INPUT' && (type === 'number' || type === 'date')) return;
      // Nella ricerca cliente Enter sceglie il primo risultato, non salta oltre
      if (step === 'cliente') {
        const primo = clienti[0];
        if (primo) scegliCliente(primo);
        return;
      }
      next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, step, clienti, puoAvanzare]);

  const pct = ((idx + 1) / STEPS.length) * 100;

  if (created) {
    return (
      <ProjectCreatedScreen
        project={created}
        onClose={() => { onClose(); navigate(`/projects/${created.id}`); }}
        onCreateQuote={() => {
          onClose();
          // Zero re-input: se sappiamo quanto è stato venduto, la riga c'è già
          const lines = answers.valore && answers.valore > 0
            ? [{ description: answers.name, quantity: 1, unitPrice: answers.valore, vatRate: 22 }]
            : undefined;
          useModalStore.getState().open('newQuote', {
            clientId: created.clientId,
            projectId: created.id,
            lines,
            title: `${answers.name} — Proposta commerciale`,
          });
        }}
        onJustOpen={() => { onClose(); navigate(`/projects/${created.id}`); }}
      />
    );
  }

  return (
    <div className={`tf${closing ? ' tf--leaving' : ''}`} ref={tfRef} role="dialog" aria-modal="true">
      <div className="tf-topbar">
        <div className="flex gap-2.5 items-center">
          <div className="h-[22px] grid place-items-center bg-ink rounded-1 px-2"><img src="/logos/m-white.svg" alt="Masamune" className="h-[9px] w-auto block" /></div>
          <div>
            <div className="font-bold text-[13px] tracking-[0.14em] uppercase">Nuovo progetto</div>
            <div className="mute text-[12px]">{QUANTE_DOMANDE} domande · premi <span className="t-kbd">Enter</span> per continuare</div>
            {/* Da dove arriva: solo display, nessun campo bloccato */}
            {dealId && <div className="mute text-[12px]">Dalla trattativa vinta{dealTitle ? ` "${dealTitle}"` : ''}</div>}
            {fromQuoteId && <div className="mute text-[12px]">Da preventivo <span className="mono">{fromQuoteId}</span></div>}
          </div>
        </div>
        <div className="tf-progress"><span style={{ width: `${pct}%` }} /></div>
        <div className="tf-step-count">{String(idx + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')}</div>
        <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
      </div>

      <div className="tf-body">
        <div className="tf-frame tf-enter-anim" key={step}>
          <div className="tf-step-label">
            <span className="num">{String(idx + 1).padStart(2, '0')}</span>
            {STEP_TITLE[step]}
          </div>

          {step === 'cliente' && (
            <>
              <h2 className="tf-question">Per quale cliente?</h2>
              <div className="tf-hint">
                Cerca per ragione sociale, partita IVA o settore. Ci sono tutti: {page?.total ?? '—'} in anagrafica.
              </div>

              {/* La seconda strada, nella stessa schermata: da una trattativa
                  vinta, con i dati già dentro. Compare solo se ce n'è almeno
                  una da convertire, altrimenti è una scatola vuota. */}
              {vinte.length > 0 && !search.trim() && (
                <div className="mb-4">
                  <div className="t-micro mb-2">Oppure parti da una trattativa vinta</div>
                  <div className="tf-options" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {vinte.slice(0, 6).map((d) => (
                      <div key={d.id} className="tf-option" onClick={() => partiDaTrattativa(d)}>
                        <div className="tf-option-body">
                          <span>{d.title}</span>
                          <span className="tf-option-sub">
                            {[
                              // Il nome del cliente sta in anagrafica: sulla
                              // trattativa `companyName` è pieno solo quando il
                              // cliente NON è ancora stato collegato.
                              tutti.find((c) => c.id === d.clientId)?.shortName
                                ?? tutti.find((c) => c.id === d.clientId)?.legalName
                                ?? d.companyName,
                              d.value > 0 ? `venduto a € ${fmtEur(d.value)}` : null,
                              d.expectedCloseDate ? `prevista ${fmtDate(d.expectedCloseDate)}` : null,
                            ].filter(Boolean).join(' · ')}
                          </span>
                        </div>
                        <Icon name="arrowRight" size={14} />
                      </div>
                    ))}
                  </div>
                  <div className="mute text-[12px] mt-2">
                    Aprendo il progetto quel valore esce dal potenziale commerciale delle Trattative: non si conta due volte.
                  </div>
                </div>
              )}
              <div className="tb-search mb-3" style={{ width: '100%', margin: '0 0 12px' }}>
                <Icon name="search" size={12} />
                <input
                  autoFocus
                  placeholder="Cerca un cliente…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Cerca un cliente"
                />
              </div>
              {clientiLoading ? (
                <div className="mute text-[13px]">Carico l'anagrafica…</div>
              ) : clienti.length === 0 ? (
                <div className="mute text-[13px]">
                  Nessun cliente per "{search}".{' '}
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => useModalStore.getState().open('newClient')}
                  >
                    <Icon name="plus" size={10} /> Crea l'anagrafica
                  </button>
                </div>
              ) : (
                <>
                  <div className="tf-options" style={{ maxHeight: 340, overflowY: 'auto' }}>
                    {clienti.map((c) => (
                      <div
                        key={c.id}
                        className="tf-option"
                        aria-selected={answers.cliente === c.id}
                        onClick={() => scegliCliente(c)}
                      >
                        <div className="tf-option-body">
                          <span>{c.legalName}</span>
                          <span className="tf-option-sub">
                            {[c.sector, c.projectsCount ? `${c.projectsCount} ${c.projectsCount === 1 ? 'progetto' : 'progetti'}` : null].filter(Boolean).join(' · ') || 'Nessun progetto ancora'}
                          </span>
                        </div>
                        <Icon name="arrowRight" size={14} />
                      </div>
                    ))}
                  </div>
                  <div className="mute text-[12px] mt-2">
                    {clienti.length} {clienti.length === 1 ? 'risultato' : 'risultati'}
                    {debSearch ? ` su ${page?.total ?? tutti.length}` : ''} · <span className="t-kbd">Enter</span> sceglie il primo
                  </div>
                </>
              )}
            </>
          )}

          {step === 'cosa' && (
            <>
              <h2 className="tf-question">Cosa gli vendiamo?</h2>
              <div className="tf-hint">La divisione che lo consegna e il modello commerciale. Si può cambiare dopo.</div>
              <div className="t-micro mb-2">Divisione</div>
              <div className="grid grid-cols-2 gap-2">
                {DIVISIONI.map(([k, d]) => (
                  <div
                    key={k}
                    className="tf-option"
                    aria-selected={answers.divisione === k}
                    onClick={() => setAnswers({ ...answers, divisione: k })}
                    style={{ padding: '12px 14px' }}
                  >
                    <div className="tf-option-body">
                      <span>{DIVISION_LABEL[k]}</span>
                      <span className="tf-option-sub">{d}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="t-micro mb-2 mt-5">Modello</div>
              <div className="grid grid-cols-3 gap-2">
                {MODELLI.map(([k, l]) => (
                  <div
                    key={k}
                    className="tf-option"
                    aria-selected={answers.tipologia === k}
                    onClick={() => setAnswers({ ...answers, tipologia: k })}
                    style={{ padding: '10px 12px' }}
                  >
                    <div className="tf-option-body"><span>{l}</span></div>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 'nome' && (
            <>
              <h2 className="tf-question">Come si chiamerà il progetto?</h2>
              <div className="tf-hint">Quello che dirai a voce per riconoscerlo. Esempio: "Aureliana — rifacimento ecommerce".</div>
              <input
                className="inp text-[20px] py-3"
                value={answers.name}
                onChange={(e) => setAnswers({ ...answers, name: e.target.value })}
                autoFocus
              />
            </>
          )}

          {step === 'consegna' && (
            <>
              <h2 className="tf-question">Quando si consegna?</h2>
              <div className="tf-hint">Solo le date che servono per sapere se siamo in ritardo. Si possono lasciare vuote.</div>
              <div className="grid grid-cols-2 gap-3 max-w-[480px]">
                <div>
                  <label className="field-label">Si parte il</label>
                  <input className="inp mono" type="date" value={answers.startDate} onChange={(e) => setAnswers({ ...answers, startDate: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Consegna prevista</label>
                  <input className="inp mono" type="date" autoFocus value={answers.deadline} onChange={(e) => setAnswers({ ...answers, deadline: e.target.value })} />
                </div>
              </div>
              {dealId && answers.valore != null && (
                <div className="adm-note mt-5 max-w-[560px]">
                  <Icon name="euro" size={12} className="mute" />
                  <span>
                    Venduto a <strong>€ {fmtEur(answers.valore, true)}</strong>: è quello che diceva la trattativa.
                    Costi, team e milestone si mettono dopo, dalla scheda del progetto.
                  </span>
                </div>
              )}
            </>
          )}

          {step === 'output' && (
            <>
              <h2 className="tf-question">Ci siamo</h2>
              <div className="tf-hint">Quattro righe, niente di più: il resto si compila lavorando.</div>
              <div className="card max-w-[600px]">
                <div className="card-bd space-y-2">
                  <SummaryRow label="Cliente" value={clienteScelto?.legalName ?? '—'} />
                  <SummaryRow label="Cosa si vende" value={answers.divisione ? `${DIVISION_LABEL[answers.divisione]} · ${(answers.tipologia ?? '').toLowerCase().replace(/_/g, ' ')}` : '—'} />
                  <SummaryRow label="Nome progetto" value={answers.name.trim() || '—'} />
                  <SummaryRow label="Consegna" value={answers.deadline ? fmtDate(answers.deadline) : 'da decidere'} />
                  {answers.valore != null && <SummaryRow label="Venduto a" value={`€ ${fmtEur(answers.valore, true)}`} />}
                </div>
              </div>
              {dealId && canonePrevisto && (
                <div className="adm-note mt-4 max-w-[600px]">
                  <Icon name="repeat" size={12} className="mute" />
                  <span>
                    Insieme al progetto nasce il canone di gestione pratica:{' '}
                    <strong>€ {fmtEur(canonePrevisto.amountNet)} al mese per {canonePrevisto.mesi} mesi</strong>{' '}
                    (€ {fmtEur(canonePrevisto.totale, true)} in tutto). Va nei ricavi ricorrenti, non dentro il valore del progetto.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="tf-footer">
        <div className="tf-hint-keys">
          <span><span className="t-kbd">↵</span> Enter</span>
          <span><span className="t-kbd">Esc</span> Annulla</span>
        </div>
        <div className="flex gap-2">
          <button className="btn" onClick={prev} disabled={idx === 0}><Icon name="arrowLeft" size={12} /> Indietro</button>
          {step !== 'output' ? (
            <button className="btn btn-primary" onClick={next} disabled={!puoAvanzare}>Avanti <Icon name="arrowRight" size={12} /></button>
          ) : (
            <button className="btn btn-primary" onClick={finalize} disabled={createMut.isPending}>
              <Icon name="check" size={12} /> {createMut.isPending ? 'Creazione…' : 'Crea progetto'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="mute text-[13px]">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Schermata di arrivo: cosa si fa adesso che il progetto esiste.
// ════════════════════════════════════════════════════════════════════
function ProjectCreatedScreen({
  project, onClose, onCreateQuote, onJustOpen,
}: {
  project: { id: string; code?: string; name: string; clientId: string; canone: ConversionResult['canone'] };
  onClose: () => void;
  onCreateQuote: () => void;
  onJustOpen: () => void;
}) {
  return (
    <div className="tf">
      <div className="tf-topbar">
        <div className="flex gap-2.5 items-center">
          <div className="w-[22px] h-[22px] grid place-items-center bg-ok text-white font-bold rounded-1">✓</div>
          <div>
            <div className="font-bold text-[13px] tracking-[0.14em] uppercase">Progetto creato</div>
            <div className="mute text-[12px]">Cosa vuoi fare adesso?</div>
          </div>
        </div>
        <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
      </div>

      <div className="tf-body">
        <div className="tf-frame tf-enter-anim">
          <h2 className="tf-question">{project.name} è aperto</h2>
          <div className="tf-hint">
            {project.code && <>Codice progetto: <span className="mono font-semibold">{project.code}</span>. </>}
            Adesso il passo logico è <strong>preparare un preventivo</strong> da far firmare al cliente —
            quando firmato lo convertirai in fattura con un click.
            <Helper text="Il flusso consigliato è: 1) Crea progetto (per tracciare costi/marginalità), 2) Crea preventivo (per la trattativa commerciale), 3) Cliente firma online, 4) Converti in fattura. Tutti i passaggi sono collegati automaticamente." />
          </div>

          {/* Sul voucher insieme al progetto è nato anche il canone: va detto,
              altrimenti nessuno sa che quella riga esiste. */}
          {project.canone && (
            <div className="adm-note mt-4 max-w-[640px]">
              <Icon name="repeat" size={12} className="mute" />
              <span>
                È nato anche <strong>{project.canone.name}</strong>: € {fmtEur(project.canone.amountNet)} al mese
                per {project.canone.mesi} mesi{project.canone.endDate ? `, fino al ${fmtDate(project.canone.endDate)}` : ''}.
                Lo trovi in Amministrazione, fra i ricavi ricorrenti.
              </span>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mt-6 max-w-[760px]">
            <ActionCard
              icon="file"
              accent="accent"
              title="Crea preventivo"
              sub="Wizard guidato — pre-popolato con cliente e progetto"
              cta="Inizia"
              onClick={onCreateQuote}
              primary
            />
            <ActionCard
              icon="arrowRight"
              accent="ink"
              title="Apri il progetto"
              sub="Vai alla scheda dettaglio per gestire team, milestone, task"
              cta="Apri"
              onClick={onJustOpen}
            />
            <ActionCard
              icon="x"
              accent="ghost"
              title="Solo chiudi"
              sub="Tornerai dove eri prima"
              cta="Chiudi"
              onClick={onClose}
            />
          </div>

          <div className="mt-8 p-4 rounded-1 bg-paper-2 text-[13px] leading-[1.6] max-w-[600px]">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon name="bell" size={12} />
              <strong>Pro-tip</strong>
            </div>
            Anche dopo, dalla scheda progetto trovi sempre la sezione "Documenti commerciali"
            dove puoi creare preventivi e fatture collegati a questo progetto.
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionCard({
  icon, accent, title, sub, cta, onClick, primary,
}: {
  icon: 'file' | 'arrowRight' | 'x';
  accent: 'accent' | 'ink' | 'ghost';
  title: string;
  sub: string;
  cta: string;
  onClick: () => void;
  primary?: boolean;
}) {
  // icon-tile = quadrato viola soft di default; ink/ghost restano neutri.
  const iconClass = accent === 'accent' ? 'icon-tile sm' : accent === 'ink' ? 'bg-ink text-white' : 'bg-paper-2 text-mute-2';
  return (
    <button
      type="button"
      className={`text-left p-4 rounded-2 border transition hover:shadow-[var(--elev-card)] ${primary ? 'border-ink' : 'border-line hover:border-ink/40'}`}
      onClick={onClick}
    >
      <div className={`grid place-items-center mb-3 ${accent === 'accent' ? '' : 'w-9 h-9 rounded-2'} ${iconClass}`}>
        <Icon name={icon} size={16} />
      </div>
      <div className="font-semibold mb-1">{title}</div>
      <div className="mute text-[13px] leading-[1.5] mb-3">{sub}</div>
      <div className={`text-[13px] font-semibold ${primary ? 'text-ink' : 'mute'}`}>
        {cta} <Icon name="arrowRight" size={10} />
      </div>
    </button>
  );
}
